
import { supabase } from '../integrations/supabase/client';
import { imageStorageService } from './imageStorageService';

export interface EnhancedTargetNote {
  id: string;
  target_code: string;
  session_id: string;
  user_id: string;
  note_type: 'text' | 'image';
  note_content: string;
  file_path?: string;
  content_type?: string;
  file_size?: number;
  submitted_at: string;
}

export const enhancedTargetNotesService = {
  // Store a text note
  storeTextNote: async (
    targetCode: string,
    sessionId: string,
    noteContent: string
  ): Promise<EnhancedTargetNote | null> => {
    try {
      console.log('📝 enhancedTargetNotesService: Storing text note:', { targetCode, sessionId });
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ enhancedTargetNotesService: Auth error:', authError);
        return null;
      }
      
      const { data, error } = await supabase
        .from('target_notes')
        .insert({
          target_code: targetCode,
          session_id: sessionId,
          user_id: user.id,
          note_type: 'text',
          note_content: noteContent
        })
        .select()
        .single();

      if (error) {
        console.error('❌ enhancedTargetNotesService: Database error storing text note:', error);
        return null;
      }

      console.log('✅ enhancedTargetNotesService: Text note stored successfully:', data);
      return data as EnhancedTargetNote;
    } catch (error) {
      console.error('❌ enhancedTargetNotesService: Exception storing text note:', error);
      return null;
    }
  },

  // Store an image note with file upload
  storeImageNote: async (
    targetCode: string,
    sessionId: string,
    imageFile: File,
    noteContent?: string
  ): Promise<EnhancedTargetNote | null> => {
    try {
      console.log('🖼️ enhancedTargetNotesService: Storing image note:', { 
        targetCode, 
        sessionId, 
        fileName: imageFile.name,
        fileSize: imageFile.size 
      });
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ enhancedTargetNotesService: Auth error:', authError);
        return null;
      }

      // Upload image to storage using the new service
      const filePath = await imageStorageService.uploadTargetImage(imageFile, user.id, targetCode);
      if (!filePath) {
        console.error('❌ enhancedTargetNotesService: Failed to upload image');
        return null;
      }

      // Store note with file metadata
      const { data, error } = await supabase
        .from('target_notes')
        .insert({
          target_code: targetCode,
          session_id: sessionId,
          user_id: user.id,
          note_type: 'image',
          note_content: noteContent || `Image: ${imageFile.name}`,
          file_path: filePath,
          content_type: imageFile.type,
          file_size: imageFile.size
        })
        .select()
        .single();

      if (error) {
        console.error('❌ enhancedTargetNotesService: Database error storing image note:', error);
        // Clean up uploaded file if database insert failed
        await imageStorageService.deleteImage(filePath);
        return null;
      }

      console.log('✅ enhancedTargetNotesService: Image note stored successfully:', data);
      return data as EnhancedTargetNote;
    } catch (error) {
      console.error('❌ enhancedTargetNotesService: Exception storing image note:', error);
      return null;
    }
  },

  // Get all notes for a target code with public URLs for images
  getNotesByTargetCode: async (targetCode: string): Promise<EnhancedTargetNote[]> => {
    try {
      console.log('📝 enhancedTargetNotesService: Getting notes for target code:', targetCode);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('❌ enhancedTargetNotesService: Auth error:', authError);
        return [];
      }
      
      const { data, error } = await supabase
        .from('target_notes')
        .select('*')
        .eq('target_code', targetCode)
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: true });

      if (error) {
        console.error('❌ enhancedTargetNotesService: Database error getting notes:', error);
        return [];
      }

      console.log('✅ enhancedTargetNotesService: Notes retrieved:', data?.length || 0);
      return (data as EnhancedTargetNote[]) || [];
    } catch (error) {
      console.error('❌ enhancedTargetNotesService: Exception getting notes:', error);
      return [];
    }
  },

  // Format notes for AI analysis - separates text and image data cleanly
  formatNotesForAnalysis: (notes: EnhancedTargetNote[]): { 
    textContent: string, 
    imageUrls: string[],
    imageMetadata: Array<{timestamp: string, filename: string}>
  } => {
    if (notes.length === 0) {
      return { textContent: '', imageUrls: [], imageMetadata: [] };
    }

    const textNotes = notes.filter(note => note.note_type === 'text');
    const imageNotes = notes.filter(note => note.note_type === 'image');

    let textContent = '';
    const imageUrls: string[] = [];
    const imageMetadata: Array<{timestamp: string, filename: string}> = [];

    // Only include pure text impressions in textContent
    if (textNotes.length > 0) {
      textContent += `USER'S WRITTEN IMPRESSIONS:\n`;
      textNotes.forEach((note, index) => {
        const timestamp = new Date(note.submitted_at).toLocaleTimeString();
        textContent += `${index + 1}. [${timestamp}] "${note.note_content.trim()}"\n`;
      });
    }

    // Process images separately - only collect URLs and metadata
    if (imageNotes.length > 0) {
      imageNotes.forEach((note) => {
        const timestamp = new Date(note.submitted_at).toLocaleTimeString();
        imageMetadata.push({
          timestamp,
          filename: note.note_content // This contains the filename/description
        });
        
        // Add public URL for AI vision analysis
        if (note.file_path) {
          const publicUrl = imageStorageService.getPublicUrl(note.file_path);
          imageUrls.push(publicUrl);
        }
      });
    }

    return { textContent, imageUrls, imageMetadata };
  }
};
